import { bracketRoundName } from "@/utils/league-bracket";

export const BRACKET = {
  padX: 0,
  padY: 16,
  boxW: 153,
  rowH: 34,
  innerGap: 16,
  laneStub: 16,
  mergeGap: 165,
  round2Extend: 30,
  champGap: 160,
  headingH: 44,
  headingGap: 0,
  thirdGap: 0
};

// Computes the geometry for a classic single-elimination bracket from the `rounds` array
// produced by bracketRounds (chronological, final last).
//
// Round 1 is drawn as boxed squad rows ending in a right-edge elbow. Every matchup (round 1
// boxes too) emits a horizontal winner lane from the middle of its connector. That lane
// carries the winning squad's name and runs to the next round, where the two incoming lanes
// terminate on a vertical merge connector; a new winner lane juts out of its middle. The final
// round's lane becomes the champion line, which struts out to where the champion name sits.
//
// An optional `consolation` race ({ homeSquad, awaySquad, bracketSlot: "THIRD" }) is drawn
// standalone below the bracket with its own heading and connector, detached from the
// elimination lanes.
export function computeBracketLayout (rounds, consolation) {
  const base = baseLayout(rounds);
  if (!consolation) return base;

  const { padY, boxW, rowH, laneStub, thirdGap, headingH, headingGap } = BRACKET;
  const top = base.height + thirdGap;
  const y1u = top + headingH + headingGap + rowH;
  const y2u = y1u + rowH;
  const mid = (y1u + y2u) / 2;
  const x = base.champion.x;
  const champX = base.champion.lineEnd;

  const lines = [
    ...base.lines,
    { x1: x, y1: y1u, x2: x, y2: y2u },
    { x1: x, y1: mid, x2: champX, y2: mid }
  ];

  const thirdPlace = {
    bracketSlot: "THIRD",
    xLeft: x - boxW,
    xRight: x,
    y1u,
    y2u,
    mid,
    laneX: x + laneStub
  };

  const races = base.races;
  const height = y2u + padY;

  return {
    ...base,
    races,
    lines,
    height,
    thirdPlace,
    thirdWinner: { x, lineEnd: champX, mid },
    width: base.width
  };
}

function baseLayout (rounds) {
  const empty = { width: 0, height: 0, races: [], lines: [], champion: null, headings: [] };
  if (!rounds || rounds.length === 0) return empty;

  const { padX, padY, boxW, rowH, innerGap, mergeGap, champGap, round2Extend, headingH, headingGap } = BRACKET;
  const rowPitch = 2 * rowH + innerGap;
  const numRounds = rounds.length;
  const last = numRounds - 1;

  // Round-1 lanes span `mergeGap`; round-2+ lanes are extended by `round2Extend`.
  const connX = (r) => {
    if (r === 0) return padX + boxW;
    return padX + boxW + mergeGap + (r - 1) * (mergeGap + round2Extend);
  };

  const positions = [];
  for (let r = 0; r < numRounds; r++) {
    const row = [];
    for (let k = 0; k < rounds[r].races.length; k++) {
      let y1u;
      let y2u;
      if (r === 0) {
        y1u = headingH + headingGap + k * rowPitch + rowH;
        y2u = y1u + rowH;
      } else {
        y1u = positions[r - 1][2 * k].mid;
        y2u = positions[r - 1][2 * k + 1].mid;
      }
      row.push({ y1u, y2u, mid: (y1u + y2u) / 2 });
    }
    positions.push(row);
  }

  const races = [];
  const lines = [];
  for (let r = 0; r < numRounds; r++) {
    const x = connX(r);
    const xLeft = r === 0 ? padX : x;
    for (let k = 0; k < rounds[r].races.length; k++) {
      const pos = positions[r][k];
      races.push({
        bracketSlot: rounds[r].races[k].bracketSlot,
        roundIndex: r,
        index: k,
        xLeft,
        xRight: x,
        y1u: pos.y1u,
        y2u: pos.y2u,
        mid: pos.mid
      });
      lines.push({ x1: x, y1: pos.y1u, x2: x, y2: pos.y2u });
      if (r < last) {
        lines.push({ x1: x, y1: pos.mid, x2: connX(r + 1), y2: pos.mid });
      }
    }
  }

  const finalPos = positions[last][0];
  const x = connX(last);
  const champX = x + champGap;
  lines.push({ x1: x, y1: finalPos.mid, x2: champX, y2: finalPos.mid });
  const champion = { x, lineEnd: champX, mid: finalPos.mid };

  const round1Count = rounds[0].races.length;
  const height = headingH + padY * 2 + (round1Count - 1) * rowPitch + 2 * rowH;

  const headings = [];
  for (let r = 0; r < numRounds; r++) {
    const x = r === 0 ? padX : connX(r - 1);
    const width = r === 0 ? boxW : connX(r) - connX(r - 1);
    headings.push({ round: r + 1, label: roundLabel(r, numRounds), x, width });
  }

  const width = champion.lineEnd;

  return { width, height, races, lines, champion, headings };
}

function roundLabel (index, numRounds) {
  return bracketRoundName(numRounds, index + 1);
}